import { Edge, Node } from 'reactflow';
import { StateController } from 'state-controller';
import { ProductionWorkflowService } from 'services/production-workflow.service';
import { BreadcrumbItem, ItemType } from 'components/ui-new/breadcrumbs/components/helpers/helpers';
import {
  GroupByEnum,
  ProductionWorkflowInfo,
  ProductionWorkflowHierarchy,
  ProductionWorkflowUpdateBody,
  ProductionWorkflowResponsibleT,
  ProductionWorkflowTaskResponsibility,
  ProductionWorkflowTagModel,
  ProductionWorkflowAdditionalTaskT,
} from 'services/production-workflow.model';
import { PaginationMode } from 'pages/production/controllers/production-filters-controller/constants';
import { ProductionStatusEnum, TaskStatusEnum, UserStatusEnum } from 'types/status-enums';
import { AppState, GetStateFunction } from 'redux/store';
import { CancelProductionActions } from 'pages/production/controllers/cancel-production-modal.controller';
import { HIGHLIGHTED_SLOT } from 'pages/production/controllers/constants';
import { Actions as ProductionAdditionalTasksActions } from 'pages/production-workflow/controllers/production-workflow-additional-tasks.controller';
import { ProductionTaskService } from 'services/production-task.service';
import { User, UserShortModel } from 'services/user.model';
import { UserService } from 'services/user.service';
import { TaskAssigmentService } from 'services/task-assigment.service';
import { TaskSuggestedUsersModel } from 'services/task-responsibility.model';
import { PAGE_SIZE } from 'components/ui-new/dropdown-user-search-selector/dropdown-user-search-selector';
import { ProductsLaunchModalActions } from 'pages/production/controllers/products-launch-modal-controller/products-launch-modal.controller';
import {
  getAllIssues,
  generateNodes,
  formatToISODate,
  getParentAndChildProductions,
  shouldTriggerDeadlineWarning,
  getAdditionalTasksIssues,
} from 'pages/production-workflow/controllers/helpers';
import { IdName } from 'types/common-types';
import { StopProductionActions } from 'pages/production/controllers/stop-production-modal.controller';
import { ProductionListActions } from 'pages/production/controllers/production-list-controller/production-list.controller';
import { ProductionFiltersActions } from 'pages/production/controllers/production-filters-controller/production-filters.controller';
import { Page } from 'pages/production/controllers/production-list-controller/types';
import { PriorityEnum } from 'types/priority-enums';
import {
  UpdateDeadlineArgs,
  HandleProductionStatusArgs,
  UpdateProductionStatusArgs,
  OnConfirmUpdateDeadlineArgs,
  SilentLoadProductionWorkflowArgs,
  WebsocketResponseMessageForProductionWorkflowT,
  ProductionWorkflowWebsocketEventCategory,
  FinishedAutoAssignWebsocketResponseT,
  WebsocketStartEndMessageForTaskStatusT,
  UpdatePlannedStartDateArgs,
} from 'pages/production-workflow/controllers/types';
import { AdditionalComponentsActions } from 'pages/production-workflow/controllers/production-workflow-additional-components.controller';
import { LocationTheProductionStatusIsChangingFrom, WebsocketEvent } from 'types/common-enums';
import { DeleteConfirmationOwnProps } from 'modules/root-modals/modals/confirmation-modal/confirmation-modal';
import { MODALS } from 'modules/root-modals/modals';
import { ModalActions } from 'modules/root-modals/root-modals.controller';
import { EmojiWithLaptop } from 'icons/emoji-with-laptop';
import DeadlineItemBodyModal from 'pages/production/components/info-dropdown/components/deadline-item-body-modal/deadline-item-body-modal';
import { loadData } from 'pages/production/components/info-dropdown/use-info-dropdown';
import { CancelProductionWithFromStockOrToDoStatusModalActions } from 'pages/production/controllers/cancel-production-with-from-stock-or-to-do-status.controller';
import ConfirmationWindow from 'modules/confirmation-window/confirmation-window';
import { Actions as ConfirmationWindowActions } from 'modules/confirmation-window/confirmation-window.controller';
import FireMinusIcon from 'icons/fire-minus';
import { TaskReopenModel, UpdateTaskBody } from 'services/production-task.model';
import { notify } from '../../../notifications';
import { router } from '../../../index';

export type TaskData = {
  id: string;
  name: string;
  disable: boolean;
  issues: IdName[];
  locked: boolean;
  task_key: string;
  time_limit: number;
  finishedAt: string;
  isReopened: boolean;
  is_in_queue: boolean;
  status: TaskStatusEnum;
  departmetsAll?: IdName[];
  total_in_progress_time: number;
  is_reopened_with_payment: boolean;
  is_reporting_period_closed: boolean;
  productionStatus: ProductionStatusEnum;
  responsibilities: ProductionWorkflowTaskResponsibility[];
};

export type NestedWorkflowData = {
  id: string;
  name: string;
  version: number;
  workflow: string;
  progress: number;
  issues: IdName[];
  finishedAt: string;
  wasChanged: boolean;
  variantName: string;
  configuration: string;
  productVariantId: string;
  isRootCompleted: boolean;
  status: ProductionStatusEnum;
  productionStatus: ProductionStatusEnum;
  responsible: ProductionWorkflowResponsibleT | null;
};

export type DeadlineUpdateBody = {
  day: number;
  year: number;
  hours: number;
  month: number;
  minutes: number;
};

export type TaskNodeType = Node<TaskData, 'task'>;

export type WorkflowNodeType = Node<NestedWorkflowData, 'workflow'>;

export type Nodes = Array<TaskNodeType | WorkflowNodeType>;

export type ProductionWorkflowState = {
  nodes: Nodes;
  edges: Edge[];
  users: User[];
  isLoading: boolean;
  allIssues: string[];
  isFullScreen: boolean;
  highlightedSlotId: string;
  breadcrumbs: BreadcrumbItem[];
  isLoadingUsersWithSuggested: boolean;
  taskAutoAssignmentEnabled: boolean;
  productionInfo: ProductionWorkflowInfo;
  usersWithSuggested: TaskSuggestedUsersModel;
  productionWorkflowHierarchy: {
    isLoading: boolean;
    hierarchy: ProductionWorkflowHierarchy[];
  };
  selectedProductionsToUpdateDeadline: string[];
  isDeadlineWarningActive: boolean;
};

const defaultState: ProductionWorkflowState = {
  nodes: [],
  edges: [],
  users: [],
  allIssues: [],
  breadcrumbs: [],
  isLoading: true,
  isFullScreen: false,
  highlightedSlotId: '',
  isLoadingUsersWithSuggested: false,
  taskAutoAssignmentEnabled: false,
  usersWithSuggested: {
    otherUsers: [],
    suggestedUsers: [],
  },
  productionInfo: {
    id: '',
    tags: [],
    edges: [],
    notes: [],
    title: '',
    issues: [],
    version: 0,
    vendor: '',
    progress: 0,
    created_at: '',
    started_at: '',
    image_url: null,
    breadcrumbs: [],
    deadline_at: '',
    finished_at: '',
    product_name: '',
    product_type: '',
    production_key: '',
    is_external: false,
    variantsHistory: [],
    deadlineHistory: [],
    product_meta_id: '',
    is_additional: false,
    rolled_back_at: null,
    additionalComponents: [],
    parent_product_meta_ids: [],
    productionWorkflowItems: [],
    is_launch_in_progress: false,
    externalOrderNumberHistory: [],
    additionalComponentsProgress: 0,
    hierarchical_tasks_count: 0,
    hierarchical_completed_tasks_count: 0,
    priority: PriorityEnum.Medium,
    is_root_workflow_completed: false,
    status: ProductionStatusEnum.To_Do,
    variant: { id: '', name: '', sku: '' },
    configuration: { id: '', name: '', sku: '' },
    main_workflow_status: ProductionStatusEnum.To_Do,
    responsible: {
      id: '',
      last_name: '',
      first_name: '',
      avatar_image_url: '',
      user_position_slots: [],
    },
    order: {
      id: '',
      order_key: '',
      client_name: '',
      line_item_id: '',
      external_order_id: '',
      external_system_name: '',
      external_order_number: '',
      marketplace_order_number: '',
      priority: PriorityEnum.Medium,
      client: {
        id: '',
        name: '',
        email: '',
        phone: '',
        company: '',
        external_client_id: '',
      },
    },
  },
  productionWorkflowHierarchy: {
    isLoading: false,
    hierarchy: [],
  },
  selectedProductionsToUpdateDeadline: [],
  isDeadlineWarningActive: false,
};

const stateController = new StateController<ProductionWorkflowState>('PRODUCTION_WORKFLOW', defaultState);

export class Actions {
  public static initWorkflowPage(id: string) {
    return async (dispatch) => {
      try {
        const [data, users, additionalTasks] = await Promise.all([
          ProductionWorkflowService.getProductionWorkflowInfo(id),
          UserService.getAllUsers({ skip: 0, take: PAGE_SIZE, status: [UserStatusEnum.Active] }),
          ProductionWorkflowService.getAdditionalTasks(id),
        ]);

        const nodes = data.productionWorkflowItems.flatMap((item) =>
          generateNodes(item, data.status, data.is_root_workflow_completed),
        );
        const additionalTaskIssues: string[] = [];

        const additionalTaskWithIssues = additionalTasks
          .filter(
            (task) =>
              task.reopenedTasks?.length || !task.failed_task_id || (task.failed_task_id && task.status !== TaskStatusEnum.Done),
          )
          .map((task) => {
            const taskIssues = getAdditionalTasksIssues(task);
            if (taskIssues.length) {
              additionalTaskIssues.push(...taskIssues.map((issue) => getAllIssues(issue, task.name, 'task_additional')));
            }

            return {
              ...task,
              issues: taskIssues,
            };
          });
        const allIssues = nodes
          .filter((node) => node.data.issues.length)
          .flatMap((node) => node.data.issues.map((issue) => getAllIssues(issue, node.data.name, node.type)));
        allIssues.push(...additionalTaskIssues);

        dispatch(ProductionAdditionalTasksActions.onChange({ tasks: additionalTaskWithIssues }));
        const highlightedSlotId = sessionStorage.getItem(HIGHLIGHTED_SLOT);

        dispatch(
          stateController.setState((prevState) => {
            return {
              ...prevState,
              allIssues,
              name: data.title,
              users: users.data,
              highlightedSlotId,
              taskAutoAssignmentEnabled: data.prefer_auto_assign_users_with_prior_experience,
              productionInfo: data,
              breadcrumbs: [
                {
                  type: 'production',
                  name: 'Production',
                },
                ...data.breadcrumbs.map((breadcrumb) => ({
                  ...breadcrumb,
                  isAllowedCopy: true,
                  isHaveTooltip: true,
                  isDisabled: breadcrumb.type === ItemType.OrderKey && true,
                })),
              ] as BreadcrumbItem[],
              edges: data.edges.map((edge) => ({
                id: edge.id,
                source: edge.source_id,
                target: edge.target_id,
                type: 'smart',
              })),
              nodes,
            };
          }),
        );
      } finally {
        sessionStorage.removeItem(HIGHLIGHTED_SLOT);
        dispatch(stateController.setState((prevState) => ({ ...prevState, isLoading: false })));
      }
    };
  }

  public static silentUpdateTags() {
    return async (dispatch, getState: GetStateFunction) => {
      const { id } = getState().production_workflow.productionInfo;

      const tags = await ProductionWorkflowService.getProductionWorkflowTags(id);

      dispatch(
        stateController.setState((prev) => ({
          ...prev,
          productionInfo: {
            ...prev.productionInfo,
            tags,
          },
        })),
      );
    };
  }

  public static applyImageToProduction(imageUrl: string | null) {
    return async (dispatch) => {
      dispatch(
        stateController.setState((prev) => ({
          ...prev,
          productionInfo: {
            ...prev.productionInfo,
            image_url: imageUrl,
          },
        })),
      );
    };
  }

  public static deleteImageFromProduction() {
    return async (dispatch) => {
      dispatch(
        stateController.setState((prev) => ({
          ...prev,
          productionInfo: {
            ...prev.productionInfo,
            image_url: null,
          },
        })),
      );
    };
  }

  public static silentUpdateAdditionalComponentTags(id: string, tag: ProductionWorkflowTagModel) {
    return async (dispatch) => {
      dispatch(
        stateController.setState((prev) => ({
          ...prev,
          productionInfo: {
            ...prev.productionInfo,
            additionalComponents: prev.productionInfo.additionalComponents.map((component) => ({
              ...component,
              tags: component.tags.some((t) => t.id === tag.id)
                ? component.tags.filter((t) => t.id !== tag.id)
                : [tag, ...component.tags],
            })),
          },
        })),
      );
    };
  }

  public static silentLoad({ id, disableAdditionalTasksSet }: SilentLoadProductionWorkflowArgs) {
    return async (dispatch) => {
      const data = await ProductionWorkflowService.getProductionWorkflowInfo(id);

      const additionalTasks = await ProductionWorkflowService.getAdditionalTasks(id);

      const nodes = data.productionWorkflowItems.flatMap((item) =>
        generateNodes(item, data.status, data.is_root_workflow_completed),
      );

      const additionalTaskIssues: string[] = [];
      const additionalTaskWithIssues = additionalTasks
        .filter(
          (task) =>
            task.reopenedTasks?.length || !task.failed_task_id || (task.failed_task_id && task.status !== TaskStatusEnum.Done),
        )
        .map((task) => {
          const taskIssues = getAdditionalTasksIssues(task);
          if (taskIssues.length) {
            additionalTaskIssues.push(...taskIssues.map((issue) => getAllIssues(issue, task.name, 'task_additional')));
          }

          const lastReopenedTask = task.reopenedTasks?.[task.reopenedTasks.length - 1] as
            | ProductionWorkflowAdditionalTaskT
            | undefined;

          const reopenedTask =
            lastReopenedTask?.id && lastReopenedTask.status === TaskStatusEnum.Done
              ? {
                  ...lastReopenedTask,
                  id: lastReopenedTask.id,
                  isFail: false,
                  disable: false,
                  isReopened: true,
                  isWithMoney: false,
                  name: lastReopenedTask.name,
                  status: lastReopenedTask.status,
                  task_key: lastReopenedTask.task_key,
                  is_in_queue: lastReopenedTask.is_in_queue,
                  taskDepartmets: lastReopenedTask.departmets_all,
                  taskResponsibilities: lastReopenedTask.taskResponsibilities,
                  failed_task_id: lastReopenedTask.failed_task_id,
                }
              : undefined;

          return {
            ...task,
            issues: taskIssues,
            isFail: false,
            isReopened: false,
            isWithMoney: false,
            disable: !!task.reopenedTasks?.length,
            reopenedTask,
          };
        });

      const allIssues = nodes
        .filter((node) => node.data.issues.length)
        .flatMap((node) => node.data.issues.map((issue) => getAllIssues(issue, node.data.name, node.type)));
      allIssues.push(...additionalTaskIssues);

      if (!disableAdditionalTasksSet) dispatch(ProductionAdditionalTasksActions.onChange({ tasks: additionalTaskWithIssues }));

      dispatch(
        stateController.setState((prevState) => ({
          ...prevState,
          productionInfo: data,
          allIssues,
          nodes,
        })),
      );
    };
  }

  public static handleProductionStatus = ({
    status,
    production,
    updatingStatusFrom,
    productionIdToUpdate,
    productionIdToLoadDataFor,
  }: HandleProductionStatusArgs) => {
    return async (dispatch, getState: () => AppState) => {
      const productionInfo = production || getState().production_workflow.productionInfo;

      const updateProduction = () => {
        if (updatingStatusFrom === LocationTheProductionStatusIsChangingFrom.WorkflowItemAdditionalProduction) {
          return dispatch(AdditionalComponentsActions.updateProduction(productionIdToUpdate, { status }));
        }

        if (production) {
          return dispatch(ProductionListActions.updateProduction(productionIdToUpdate, { status }, false));
        }

        return dispatch(Actions.updateProduction(productionIdToUpdate, { status }));
      };

      if (
        !(await dispatch(
          Actions.updateProductionStatus({
            status,
            productionIdToLoadDataFor,
            location: updatingStatusFrom,
            isMain: productionInfo.is_main,
            productionInfo: {
              id: productionIdToUpdate,
              title: productionInfo.title,
              status: productionInfo.status,
              version: productionInfo.version,
              variantId: productionInfo.variant.id,
              variantName: productionInfo.variant.name,
              configurationName: productionInfo.configuration.name,
            },
          }),
        ))
      ) {
        updateProduction();
      }
    };
  };

  public static updateProductionStatus = ({
    status,
    isMain,
    location,
    productionInfo,
    productionIdToLoadDataFor,
  }: UpdateProductionStatusArgs) => {
    return async (dispatch) => {
      if (
        (productionInfo.status === ProductionStatusEnum.To_Do || productionInfo.status === ProductionStatusEnum.From_Stock) &&
        status === ProductionStatusEnum.In_Progress
      ) {
        dispatch(
          ProductsLaunchModalActions.openModal({
            isMain,
            openedFrom: location,
            productionIdToLoadDataFor,
            version: productionInfo.version,
            variantName: productionInfo.variantName,
            productionIdToLaunch: productionInfo.id,
            productVariantId: productionInfo.variantId,
            configurationName: productionInfo.configurationName,
          }),
        );
        return true;
      }

      if (productionInfo.status === ProductionStatusEnum.In_Progress && status === ProductionStatusEnum.Stopped) {
        dispatch(StopProductionActions.openModal(productionInfo.id, productionInfo.title, location));
        return true;
      }

      if (
        (productionInfo.status === ProductionStatusEnum.In_Progress || productionInfo.status === ProductionStatusEnum.Stopped) &&
        status === ProductionStatusEnum.Canceled
      ) {
        const canCancel = await ProductionWorkflowService.getCanCancelProduction(productionInfo.id);

        dispatch(
          CancelProductionActions.openModal({
            page: location,
            productionIdToLoadDataFor,
            tasks: canCancel.tasks ?? [],
            productionId: productionInfo.id,
            productionName: productionInfo.title,
            components: canCancel.components ?? [],
          }),
        );

        return true;
      }
      if (
        (productionInfo.status === ProductionStatusEnum.From_Stock || productionInfo.status === ProductionStatusEnum.To_Do) &&
        status === ProductionStatusEnum.Canceled
      ) {
        const canCancelFromTodoOrFromStock = await ProductionWorkflowService.getCanCancelProduction(productionInfo.id);

        dispatch(
          CancelProductionWithFromStockOrToDoStatusModalActions.openModal(
            productionInfo.id,
            productionIdToLoadDataFor,
            status,
            productionInfo.title,
            location,
            canCancelFromTodoOrFromStock.tasks ?? [],
            canCancelFromTodoOrFromStock.components ?? [],
          ),
        );
        return true;
      }

      return false;
    };
  };

  public static updateProduction(id: string, value: Partial<ProductionWorkflowInfo>) {
    return async (dispatch, getState: () => AppState) => {
      const { productionInfo } = getState().production_workflow;

      const updateData: ProductionWorkflowUpdateBody = {
        title: value.title,
        status: value.status,
        priority: value.priority,
        responsible_id: value.responsible?.id || null,
      };

      try {
        dispatch(
          stateController.setState((prev) => ({
            ...prev,
            productionInfo: { ...prev.productionInfo, ...value },
          })),
        );

        await ProductionWorkflowService.update(id, updateData);
        dispatch(Actions.silentLoad({ id: productionInfo.id, disableAdditionalTasksSet: true }));
      } catch (error) {
        dispatch(stateController.setState((prev) => ({ ...prev, productionInfo })));
        notify.error(error.message);
      }
    };
  }

  public static toggleTaskAutoAssignment() {
    return async (dispatch, getState: () => AppState) => {
      const { productionInfo, taskAutoAssignmentEnabled } = getState().production_workflow;

      try {
        const response = await ProductionWorkflowService.update(productionInfo.id, {
          prefer_auto_assign_users_with_prior_experience: !taskAutoAssignmentEnabled,
        });

        dispatch(
          stateController.setState({
            taskAutoAssignmentEnabled: Boolean(response.prefer_auto_assign_users_with_prior_experience),
          }),
        );
      } catch (error) {
        notify.error(error.message);
      }
    };
  }

  public static updateCanvasNodes(id: string, value: Partial<TaskData | NestedWorkflowData>, type: 'workflow' | 'task') {
    return async (dispatch, getState: () => AppState) => {
      const { nodes, productionInfo } = getState().production_workflow;

      try {
        if (type === 'workflow') {
          const nod = nodes.find((item) => item.data.id === id);
          const oldWorkflow = nod?.data;

          const isProductionStatusUpdated = await dispatch(
            Actions.updateProductionStatus({
              location: LocationTheProductionStatusIsChangingFrom.WorkflowItem,
              productionIdToLoadDataFor: productionInfo.id,
              status: value.status as ProductionStatusEnum,
              isMain: productionInfo.is_main,
              productionInfo: {
                id,
                title: oldWorkflow?.name || '',
                status: oldWorkflow?.status as ProductionStatusEnum,
                version: (oldWorkflow as NestedWorkflowData).version,
                variantName: (oldWorkflow as NestedWorkflowData).variantName,
                variantId: (oldWorkflow as NestedWorkflowData).productVariantId,
                configurationName: (oldWorkflow as NestedWorkflowData).configuration,
              },
            }),
          );

          if (!isProductionStatusUpdated) {
            dispatch(
              stateController.setState((prev) => ({
                ...prev,
                nodes: prev.nodes.map((node) => (node.data.id === id ? { ...node, data: { ...node.data, ...value } } : node)),
              })),
            );
            await ProductionWorkflowService.update(id, {
              status: value.status,
              responsible_id: (value as NestedWorkflowData).responsible?.id || null,
            });
            if (value.status) await dispatch(Actions.silentLoad({ id: productionInfo.id }));
          }
        }
        if (type === 'task') {
          dispatch(
            stateController.setState((prev) => ({
              ...prev,
              nodes: prev.nodes.map((node) => (node.data.id === id ? { ...node, data: { ...node.data, ...value } } : node)),
            })),
          );

          if (value.status === TaskStatusEnum.Reopened) {
            const reasonOptions = await ProductionTaskService.getAllFailReasons();

            dispatch(ConfirmationWindowActions.setReasonOptions(reasonOptions));

            const targetTask = nodes.find((n) => n.data.id === id);

            if (targetTask && targetTask.type === 'task') {
              const taskToReopen: TaskReopenModel = [
                {
                  id: targetTask.id,
                  name: targetTask.data.name,
                  taskKey: targetTask.data.task_key,
                },
              ];

              dispatch(
                ModalActions.openModal<DeleteConfirmationOwnProps>({
                  id: MODALS.CONFIRM,
                  props: {
                    title: <>Apply changes?</>,
                    text: (
                      <ConfirmationWindow
                        description="Indicate the reason why task(s) didn’t pass the quality control. It will be saved and appear at production in task(s) that
                              didn’t pass the quality control."
                        tasksToReopen={taskToReopen}
                        onReasonChange={(values) => dispatch(ConfirmationWindowActions.onReasonChange(values))}
                        onReasonParametersChange={(values) =>
                          dispatch(ConfirmationWindowActions.onReasonParametersChange(values.id))
                        }
                      />
                    ),
                    icon: <FireMinusIcon />,
                    withCloseButton: false,
                    actionText: 'Apply',
                    countItems: 1,
                    action: () => dispatch(Actions.handleApplyReopenedTasksStatus(value, id, type)),
                    cancelAction: () => dispatch(ConfirmationWindowActions.disposeState()),
                    onClickAway: () => dispatch(ConfirmationWindowActions.disposeState()),
                  },
                }),
              );
            }
            return;
          }

          await ProductionTaskService.updateTask(id, {
            status: value.status,
            is_in_queue: 'is_in_queue' in value ? value.is_in_queue : undefined,
            make_assignment: false,
          });

          await dispatch(Actions.silentLoad({ id: productionInfo.id }));
        }
      } catch (error) {
        dispatch(stateController.setState((prev) => ({ ...prev, nodes })));

        if (type !== 'task') {
          notify.error(error.message);
        }
      }
    };
  }

  static handleApplyReopenedTasksStatus(
    value: Partial<TaskData | NestedWorkflowData>,
    taskId: string,
    type: 'workflow' | 'task',
  ) {
    return async (dispatch, getState: () => AppState) => {
      const { reasonForReopening, selectedReasonOptions } = getState().confirmation_window;

      const { nodes, productionInfo } = getState().production_workflow;

      try {
        const reasonIds = selectedReasonOptions.map((reason) => reason.id);

        const body: UpdateTaskBody = {
          make_assignment: false,
          ...value,
        };

        if (reasonIds && reasonIds.length > 0) {
          body.fail_reason_ids = reasonIds;
        }

        if (reasonForReopening?.trim()) {
          body.comment = reasonForReopening;
        }

        await ProductionTaskService.updateTask(taskId, body);

        await dispatch(Actions.silentLoad({ id: productionInfo.id }));

        dispatch(ConfirmationWindowActions.disposeState());
      } catch (error) {
        dispatch(stateController.setState((prev) => ({ ...prev, nodes })));

        if (type !== 'task') {
          notify.error(error.message);
        }
      }
    };
  }

  public static clearWorkflowPage() {
    return async (dispatch) => dispatch(stateController.setState(() => defaultState));
  }

  public static setIsFullScreen(value: boolean) {
    return (dispatch) => dispatch(stateController.setState((prevState) => ({ ...prevState, isFullScreen: value })));
  }

  public static deleteProductionWorkflow(id: string) {
    return async (dispatch) => {
      await ProductionWorkflowService.delete(id);
      dispatch(Actions.clearWorkflowPage());
      await router.navigate('/production');
      notify.success('Deleted production workflow successfully');
    };
  }

  public static handleTaskUses(taskId: string, user: UserShortModel, slotId: string, type: 'assign' | 'unassign') {
    return async (dispatch, getState: () => AppState) => {
      const isAssign = type === 'assign';
      const { nodes, productionInfo } = getState().production_workflow;

      try {
        dispatch(
          stateController.setState((prev) => ({
            ...prev,
            nodes: prev.nodes.map((node: TaskNodeType) => {
              return node.data.id === taskId
                ? {
                    ...node,
                    data: {
                      ...node.data,
                      responsibilities: node.data.responsibilities.map((responsibility) => ({
                        ...responsibility,
                        taskSlots: responsibility.taskSlots.map((slot) => {
                          return slot.id === slotId
                            ? {
                                ...slot,
                                task_assignment_id: isAssign ? user.id : null,
                                taskAssignment: isAssign ? { user } : null,
                              }
                            : slot;
                        }),
                      })),
                    },
                  }
                : node;
            }),
          })),
        );
        if (isAssign) {
          await TaskAssigmentService.assignUser(user.id, slotId);
        } else {
          await TaskAssigmentService.unassignUser(slotId);
        }
        dispatch(Actions.silentLoad({ id: productionInfo.id }));
      } catch (error) {
        dispatch(stateController.setState((prev) => ({ ...prev, nodes })));
        notify.error(error.message);
      }
    };
  }

  public static onSelectProductionToUpdateDeadline(productionIds: string[], newDeadline: string, targetId: string) {
    return (dispatch, getState: () => AppState) => {
      const { hierarchy } = getState().production_workflow.productionWorkflowHierarchy;
      if (hierarchy?.length === 0) {
        return;
      }
      const { parentProduction, childProductions } = getParentAndChildProductions(hierarchy);
      const isDeadlineWarningActive = shouldTriggerDeadlineWarning({
        newDeadline,
        parentProduction,
        childProductions,
        targetId,
        productionIds,
        hierarchy,
      });

      dispatch(
        stateController.setState((prevState) => ({
          ...prevState,
          selectedProductionsToUpdateDeadline: [...productionIds, targetId],
          isDeadlineWarningActive,
        })),
      );
    };
  }

  public static updateOrConfirmDeadline({
    productionId,
    newDeadlineBody,
    productionName,
    page,
    rootOrderId = '',
    rootMetaId = '',
  }: OnConfirmUpdateDeadlineArgs) {
    return async (dispatch, getState: () => AppState) => {
      const { hierarchy } = getState().production_workflow.productionWorkflowHierarchy;
      const hierarchyWithoutTargetProduction = hierarchy?.filter((item) => item.id !== productionId);
      const newDeadline = formatToISODate(newDeadlineBody);
      const isSingleItemInHierarchy = hierarchy?.length === 1;
      try {
        if (isSingleItemInHierarchy) {
          await dispatch(
            Actions.updateDeadline({ productionId, newDeadline, page, isMultiUpdate: false, rootOrderId, rootMetaId }),
          );
        } else {
          await dispatch(
            ModalActions.openModal<DeleteConfirmationOwnProps>({
              id: MODALS.CONFIRM,
              props: {
                title: <>Apply changed deadline to all production items?</>,
                actionText: 'Apply',
                text: (
                  <DeadlineItemBodyModal
                    productions={hierarchyWithoutTargetProduction}
                    onChangeProduction={(productionIds) =>
                      dispatch(Actions.onSelectProductionToUpdateDeadline(productionIds, newDeadline, productionId))
                    }
                    targetProductionName={productionName}
                    newDeadline={newDeadline}
                  />
                ),
                actionButtonColor: 'primary',
                backgroundColor: '#DBE9FF',
                icon: <EmojiWithLaptop />,
                cancelButtonText: 'Cancel',
                action: async () => {
                  await dispatch(
                    Actions.updateDeadline({ productionId, newDeadline, page, isMultiUpdate: true, rootOrderId, rootMetaId }),
                  );
                },
              },
            }),
          );
        }
      } catch (error) {
        notify.error(error.message);
      }
    };
  }

  public static updateDeadline({
    productionId,
    newDeadline,
    page,
    isMultiUpdate,
    rootOrderId = '',
    rootMetaId = '',
  }: UpdateDeadlineArgs) {
    return async (dispatch, getState: () => AppState) => {
      const { selectedProductionsToUpdateDeadline } = getState().production_workflow;
      const workflowsToUpdateDeadline = isMultiUpdate ? selectedProductionsToUpdateDeadline : [productionId];
      try {
        const updatedBody = {
          workflowsToUpdateDeadline,
          deadline: newDeadline,
        };
        await ProductionWorkflowService.updateDeadlineByHierarchy(updatedBody);

        if (page === Page.InfoDropdownWorkflow) {
          await dispatch(Actions.silentLoad({ id: productionId }));
          await loadData(productionId);
        }
        if (page === Page.InfoDropdownProduction) {
          const { groupBy } = getState().production.filters;

          await loadData(productionId);

          if (groupBy === GroupByEnum.None) {
            await dispatch(
              ProductionFiltersActions.getProductionsByFilter({
                customGroupBy: groupBy,
                showFetchEffect: false,
                resetOpenedProductionGroups: false,
                paginationMode: PaginationMode.CurrentPage,
              }),
            );
          } else {
            let groupId: string = '';
            if (groupBy === GroupByEnum.Order) {
              groupId = rootOrderId;
            } else if (groupBy === GroupByEnum.Product) {
              groupId = rootMetaId;
            }
            dispatch(ProductionListActions.getTargetGroupData(groupId));
          }
        }

        notify.success('Successfully updated');
      } catch (error) {
        notify.error(error.message);
      }
    };
  }

  public static updatePlannedStartDate({
    productionId,
    newDate,
    page,
    rootOrderId = '',
    rootMetaId = '',
  }: UpdatePlannedStartDateArgs) {
    return async (dispatch, getState: () => AppState) => {
      const newDateValue = formatToISODate(newDate);

      const updateData: ProductionWorkflowUpdateBody = {
        planned_start_date: newDateValue,
      };
      try {
        await ProductionWorkflowService.update(productionId, updateData);

        if (page === Page.InfoDropdownWorkflow) {
          await dispatch(Actions.silentLoad({ id: productionId }));
          await loadData(productionId);
        }
        if (page === Page.InfoDropdownProduction) {
          const { groupBy } = getState().production.filters;

          await loadData(productionId);

          if (groupBy === GroupByEnum.None) {
            await dispatch(
              ProductionFiltersActions.getProductionsByFilter({
                customGroupBy: groupBy,
                showFetchEffect: false,
                resetOpenedProductionGroups: false,
                paginationMode: PaginationMode.CurrentPage,
              }),
            );
          } else {
            let groupId: string = '';
            if (groupBy === GroupByEnum.Order) {
              groupId = rootOrderId;
            } else if (groupBy === GroupByEnum.Product) {
              groupId = rootMetaId;
            }
            dispatch(ProductionListActions.getTargetGroupData(groupId));
          }
        }

        notify.success('Successfully updated');
      } catch (error) {
        notify.error(error.message);
      }
    };
  }

  public static getProductionWorkflowHierarchy(id: string) {
    return async (dispatch) => {
      try {
        dispatch(
          stateController.setState((prev) => ({
            ...prev,
            productionWorkflowHierarchy: {
              ...prev.productionWorkflowHierarchy,
              isLoading: true,
            },
          })),
        );
        const data = await ProductionWorkflowService.getProductionWorkflowHierarchy({
          id,
          status: [
            ProductionStatusEnum.From_Stock,
            ProductionStatusEnum.In_Progress,
            ProductionStatusEnum.Stopped,
            ProductionStatusEnum.To_Do,
          ],
        });
        dispatch(
          stateController.setState((prev) => ({
            ...prev,
            productionWorkflowHierarchy: {
              ...prev.productionWorkflowHierarchy,
              hierarchy: data,
              isLoading: false,
            },
          })),
        );
      } catch (error) {
        dispatch(
          stateController.setState((prev) => ({
            ...prev,
            productionWorkflowHierarchy: {
              ...prev.productionWorkflowHierarchy,
              isLoading: false,
            },
          })),
        );
        notify.error(error.message);
      }
    };
  }

  // ====== WS auto assignment ==================================================================================================================

  static handleAutoAssignmentWebsocketResponse = (message: FinishedAutoAssignWebsocketResponseT, event: WebsocketEvent) => {
    return (dispatch) => {
      const { taskResponsibilities } = message;

      if (!taskResponsibilities) return;

      const isAutoAssignFinishedEvent = event === WebsocketEvent.TaskAutoAssignmentFinished;

      if (isAutoAssignFinishedEvent) {
        dispatch(
          stateController.setState((prevState) => {
            const updatedNodes = prevState.nodes.map((node: TaskNodeType) => {
              if (node.data.id === taskResponsibilities[0]?.task_id) {
                const updatedResponsibilities = node.data.responsibilities.map((resp) => {
                  if (resp.task_id === taskResponsibilities[0]?.task_id) {
                    return { ...resp, ...taskResponsibilities[0] };
                  }
                  return resp;
                });

                return {
                  ...node,
                  data: {
                    ...node.data,
                    responsibilities: updatedResponsibilities,
                  },
                };
              }
              return node;
            });

            return { ...prevState, nodes: updatedNodes };
          }),
        );
      }
    };
  };

  // ====== WS bulk status ==================================================================================================================

  static handleBulkSCViewerWsResponse = (message: WebsocketStartEndMessageForTaskStatusT, event: WebsocketEvent) => {
    return (dispatch, getState: () => AppState) => {
      if (!message || !event) {
        return;
      }

      const { nodes, productionInfo } = getState().production_workflow;
      const { tasks } = getState().production_workflow_additional_tasks;

      const isStartEvent = event === WebsocketEvent.BulkStatusChangeStarted;
      const isFinishEvent = event === WebsocketEvent.BulkStatusChangeFinished;

      if (isStartEvent && 'tasksIds' in message) {
        const allTaskIdsSet = new Set(message.tasksIds);

        const hasMatchingWorkflowTask = nodes.some((node) => allTaskIdsSet.has(node.data.id));
        const hasMatchingAdditionalTask = tasks.some(
          (t) => allTaskIdsSet.has(t.id) || t.reopenedTasks?.some((subTask) => allTaskIdsSet.has(subTask.id)),
        );

        if (!hasMatchingWorkflowTask && !hasMatchingAdditionalTask) return;

        if (hasMatchingWorkflowTask) {
          const updatedNodes = nodes.map((node) => {
            if (node.type === 'task' && allTaskIdsSet.has(node.data.id)) {
              return {
                ...node,
                data: {
                  ...node.data,
                  locked: true,
                },
              };
            }
            return node;
          });

          dispatch(
            stateController.setState((prev) => ({
              ...prev,
              nodes: updatedNodes,
            })),
          );
        }

        if (hasMatchingAdditionalTask) {
          const updatedAdditionalTasks = tasks.map((t) => {
            const isMainLocked = allTaskIdsSet.has(t.id);
            const updatedReopened = t.reopenedTasks?.map((subTask) => {
              return allTaskIdsSet.has(subTask.id) ? { ...subTask, locked: true } : subTask;
            });

            const isReopenedChanged = JSON.stringify(updatedReopened) !== JSON.stringify(t.reopenedTasks);

            if (isMainLocked || isReopenedChanged) {
              return {
                ...t,
                locked: isMainLocked ? true : t.locked,
                reopenedTasks: updatedReopened || t.reopenedTasks,
              };
            }

            return t;
          });

          dispatch(ProductionAdditionalTasksActions.updateAdditionalTaskLockedIndicator(updatedAdditionalTasks));
        }

        return;
      }

      if (isFinishEvent && 'task_ids' in message) {
        const allTaskIdsSet = new Set(message.task_ids);
        const additionalTaskStatus = message.status;

        const hasMatchingWorkflowTask = nodes.some((node) => allTaskIdsSet.has(node.data.id));
        const hasMatchingAdditionalTask = tasks.some(
          (t) => allTaskIdsSet.has(t.id) || t.reopenedTasks?.some((subTask) => allTaskIdsSet.has(subTask.id)),
        );

        if (!hasMatchingWorkflowTask && !hasMatchingAdditionalTask) return;

        if (hasMatchingWorkflowTask) {
          dispatch(Actions.silentLoad({ id: productionInfo.id }));
        }

        if (hasMatchingAdditionalTask) {
          const updatedAdditionalTasks = tasks.map((t) => {
            const isMainMatch = allTaskIdsSet.has(t.id);
            const updatedReopened = t.reopenedTasks?.map((subTask) => {
              return allTaskIdsSet.has(subTask.id) ? { ...subTask, locked: false, status: additionalTaskStatus } : subTask;
            });

            const isReopenedChanged = JSON.stringify(updatedReopened) !== JSON.stringify(t.reopenedTasks);

            if (isMainMatch || isReopenedChanged) {
              return {
                ...t,
                ...(isMainMatch && {
                  locked: false,
                  status: additionalTaskStatus,
                }),
                reopenedTasks: updatedReopened || t.reopenedTasks,
              };
            }

            return t;
          });

          dispatch(ProductionAdditionalTasksActions.updateAdditionalTaskLockedIndicator(updatedAdditionalTasks));
        }
      }
    };
  };

  // ====== WS handlers ==================================================================================================================
  static handleWebsocketResponse = (message: WebsocketResponseMessageForProductionWorkflowT, event: WebsocketEvent) => {
    return (dispatch) => {
      const eventHandlers = {
        [ProductionWorkflowWebsocketEventCategory.AUTO_ASSIGNMENT]: Actions.handleAutoAssignmentWebsocketResponse,
        [ProductionWorkflowWebsocketEventCategory.BULK_STATUS_CHANGE_VIEWER]: Actions.handleBulkSCViewerWsResponse,
      };
      const eventCategoryMapping: Partial<Record<WebsocketEvent, keyof typeof eventHandlers>> = {
        [WebsocketEvent.TaskAutoAssignmentFinished]: ProductionWorkflowWebsocketEventCategory.AUTO_ASSIGNMENT,
        [WebsocketEvent.BulkStatusChangeStarted]: ProductionWorkflowWebsocketEventCategory.BULK_STATUS_CHANGE_VIEWER,
        [WebsocketEvent.BulkStatusChangeFinished]: ProductionWorkflowWebsocketEventCategory.BULK_STATUS_CHANGE_VIEWER,
      };
      const handlerName = eventCategoryMapping[event];
      switch (handlerName) {
        case ProductionWorkflowWebsocketEventCategory.AUTO_ASSIGNMENT:
          dispatch(eventHandlers[handlerName](message as FinishedAutoAssignWebsocketResponseT, event));
          break;
        case ProductionWorkflowWebsocketEventCategory.BULK_STATUS_CHANGE_VIEWER:
          dispatch(eventHandlers[handlerName](message as WebsocketStartEndMessageForTaskStatusT, event));
          break;
        default:
      }
    };
  };
}

export class Selectors {
  public static canUserManageFailedTasks(state: AppState) {
    const productionInfo = state.production_workflow?.productionInfo;
    if (productionInfo) {
      const completed_tasks_exists =
        state.production_workflow?.productionInfo?.productionWorkflowItems.some(
          (item) =>
            (item.task?.status === TaskStatusEnum.Done && !item.task?.reopened_tasks?.length) ||
            item.task?.reopened_tasks?.some((rt) => rt.status === TaskStatusEnum.Done && !rt.failed_by),
        ) ?? false;

      return completed_tasks_exists;
    }
    return false;
  }

  public static checkIfProductionInMassLaunch(state: AppState) {
    return (
      !!state.production.productionList.launchingProductionIds.length ||
      state.production_workflow.productionInfo?.is_launch_in_progress
    );
  }
}

export const reducer = stateController.getReducer();
